import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {useApi} from '../hooks/useApi'
import {AuthContextInterface} from '../components/AuthProvider'
import {RootState} from './rootReducer'
import {LoadingStatuses} from '../components/types/LoadingStatusses'

//#region types
type SliceState = {
  error: string | null | undefined
  completions?: GuaranteeCompletions
  status: LoadingStatuses
}

export interface GuaranteeCompletions {
  flashcardsCompleted: boolean
  videosCompleted: boolean
  examTaken: boolean
  allQuizzesCompleted: boolean
  hasCompleted: boolean
  hasClaimedInLastThreeMonths: boolean
  hasBypassActive: boolean
}
//#endregion

//#region api
type ClaimGuaranteePayload = {
  auth: AuthContextInterface
  lessonPlanId: string
  examDate: string
  registrationId: string
  scoreReport: any
}

export const claimGuarantee = createAsyncThunk<any, ClaimGuaranteePayload>(
  '/guarantee/claim',
  async ({auth, lessonPlanId, examDate, registrationId, scoreReport}) => {
    const endpoint = `/v1/are/guarantee`

    const formData = new FormData()

    formData.append('examDate', examDate)
    formData.append('division', lessonPlanId)
    formData.append('registrationId', registrationId)
    formData.append('scoreReport', scoreReport)

    return useApi(auth, endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': 'Automatic',
      },
    }).then(res => res.status === 200)
  },
)

type CheckForCanClaimGuaranteePayload = {
  auth: AuthContextInterface
  course: string
  lessonPlanId: string
  examDate: string
}
export const checkForCanClaimGuarantee = createAsyncThunk<
  GuaranteeCompletions,
  CheckForCanClaimGuaranteePayload
>(
  '/guarantee/last-three-months',
  async ({auth, course, lessonPlanId, examDate}) => {
    const endpoint = `/v1/are/guarantee/last-three-months/${course}`

    const body = {
      lessonPlanId,
      examDate,
    }

    return useApi(auth, endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then(res => res.json())
  },
)
//#endregion

//#region slice
const initialState: SliceState = {
  error: undefined,
  completions: undefined,
  status: LoadingStatuses.Idle,
}

export default createSlice({
  name: 'guarantee',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(checkForCanClaimGuarantee.fulfilled, (state, action) => {
      state.completions = action.payload
      state.status = LoadingStatuses.Succeeded
    })
    builder.addCase(checkForCanClaimGuarantee.pending, state => {
      state.status = LoadingStatuses.Loading
      state.completions = undefined
    })
    builder.addCase(checkForCanClaimGuarantee.rejected, state => {
      state.status = LoadingStatuses.Failed
      state.completions = undefined
    })
  },
})
//#endregion

//#region selectors
export const selectCanClaimGuarantee = ({guarantee}: RootState) =>
  guarantee.completions

export const selectClaimEligibilityStatus = ({guarantee}: RootState) =>
  guarantee.status
//#endregion
